import { useEffect, useState } from "react";

/**
 * 5-second animated intro shown ONCE per browser session on the landing page.
 * Hero image + a subtle golden-coin drop overlay (CSS-only, no MP4 required
 * so nothing has to load — buttery on every device).
 */
const HERO_IMAGE = "https://customer-assets-eiarnc6j.emergentagent.net/job_a4eb58a9-8ca0-4dcc-ab9a-24c65265e3fd/artifacts/9rm3beoh_724466AC-566A-459E-A572-6E2DBA856E4D.png";
const SESSION_KEY = "aureon-intro-seen-v1";

// 8 golden coins with staggered starts / drift so it feels organic.
const COINS = [
  { left: "10%", delay: "0.0s", size: 34, duration: "3.2s" },
  { left: "22%", delay: "0.6s", size: 26, duration: "3.6s" },
  { left: "38%", delay: "0.2s", size: 40, duration: "3.0s" },
  { left: "50%", delay: "1.1s", size: 30, duration: "3.4s" },
  { left: "62%", delay: "0.4s", size: 36, duration: "3.1s" },
  { left: "74%", delay: "0.9s", size: 28, duration: "3.5s" },
  { left: "84%", delay: "1.5s", size: 32, duration: "3.3s" },
  { left: "92%", delay: "0.7s", size: 24, duration: "3.7s" },
];

function Coin({ size = 32 }) {
  return (
    <svg viewBox="0 0 64 64" width={size} height={size} aria-hidden>
      <defs>
        <radialGradient id="cg" cx="35%" cy="30%" r="70%">
          <stop offset="0%" stopColor="#FFF5CC" />
          <stop offset="45%" stopColor="#F5CE58" />
          <stop offset="100%" stopColor="#8C6A17" />
        </radialGradient>
      </defs>
      <circle cx="32" cy="32" r="30" fill="url(#cg)" stroke="#4A3810" strokeWidth="1.5" />
      <circle cx="32" cy="32" r="24" fill="none" stroke="#B98A28" strokeWidth="0.8" opacity="0.6" />
      <text x="32" y="41" fontFamily="Georgia, serif" fontSize="26" fontWeight="700"
            textAnchor="middle" fill="#4A3810">₿</text>
    </svg>
  );
}

export default function AureonIntro() {
  // Skip immediately if user has seen the intro in THIS browser session.
  const [visible, setVisible] = useState(() => {
    try { return typeof window !== "undefined" && !window.sessionStorage.getItem(SESSION_KEY); }
    catch { return true; }
  });

  useEffect(() => {
    if (!visible) return;
    try { window.sessionStorage.setItem(SESSION_KEY, "1"); } catch {}

    // ── Subtle golden-coin chime layered on top of the animation ──
    // Web Audio API only — no asset download, no MP3, no libraries.
    // We synthesise a short 2-tone bell (fundamental + fifth) with fast
    // decay so each ping sounds like a coin landing on marble.
    let ctx;
    const chimes = [];
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      ctx = AC ? new AC() : null;
      // Autoplay policies: try to resume; if blocked, the visual still plays.
      if (ctx && ctx.state === "suspended") { ctx.resume().catch(() => {}); }
    } catch { ctx = null; }

    const chime = (offsetSec) => {
      if (!ctx) return;
      const t0 = ctx.currentTime + offsetSec;
      // Two detuned sine oscillators → warm "clink" not a video-game blip.
      [880, 1320].forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.setValueAtTime(freq, t0);
        // Fast attack, quick decay to silence in ~0.35s
        gain.gain.setValueAtTime(0.0001, t0);
        gain.gain.exponentialRampToValueAtTime(i === 0 ? 0.18 : 0.10, t0 + 0.005);
        gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.35);
        osc.connect(gain).connect(ctx.destination);
        osc.start(t0);
        osc.stop(t0 + 0.4);
      });
    };

    // Time chimes so they land near the moment each coin should "hit" the
    // hand — offsets roughly match the visible coin animation-delays.
    const scheduleAt = [0.35, 0.9, 1.4, 2.0, 2.7, 3.3, 3.9, 4.4];
    scheduleAt.forEach((s) => chime(s));

    const hide = setTimeout(() => setVisible(false), 5000);
    return () => {
      clearTimeout(hide);
      chimes.forEach((c) => { try { c.disconnect(); } catch {} });
      try { ctx && ctx.close(); } catch {}
    };
  }, [visible]);

  if (!visible) return null;

  return (
    <div
      data-testid="aureon-intro"
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black"
      style={{ animation: "aureon-intro-fade 5s ease-in-out forwards" }}
      onClick={() => setVisible(false)}
    >
      <img
        src={HERO_IMAGE}
        alt="Aureon Global Markets"
        className="absolute inset-0 h-full w-full object-cover"
        style={{ objectPosition: "center" }}
      />

      {/* Golden coin drop overlay — CSS-only, GPU-accelerated */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
        {COINS.map((c, i) => (
          <span
            key={i}
            className="absolute -top-16 drop-shadow-[0_2px_6px_rgba(212,175,55,0.55)]"
            style={{
              left: c.left,
              animation: `aureon-coin-fall ${c.duration} cubic-bezier(0.36,0,0.56,1) ${c.delay} infinite`,
              transformOrigin: "center",
            }}
          >
            <Coin size={c.size} />
          </span>
        ))}
      </div>

      {/* Skip hint for returning power users */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setVisible(false); }}
        className="absolute bottom-6 right-6 rounded-full border border-gold/40 bg-black/40 px-4 py-1.5 text-[10px] uppercase tracking-[0.25em] text-gold backdrop-blur-sm transition-colors hover:bg-gold hover:text-ink"
        data-testid="aureon-intro-skip"
      >
        Skip →
      </button>

      <style>{`
        @keyframes aureon-intro-fade {
          0%   { opacity: 0; }
          6%   { opacity: 1; }
          90%  { opacity: 1; }
          100% { opacity: 0; visibility: hidden; }
        }
        @keyframes aureon-coin-fall {
          0%   { transform: translateY(0) rotate(0deg);     opacity: 0; }
          10%  { opacity: 1; }
          80%  { opacity: 1; }
          100% { transform: translateY(110vh) rotate(720deg); opacity: 0; }
        }
      `}</style>
    </div>
  );
}
